import { useEffect, useRef } from 'react';
import { useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';

/**
 * BoxSelector component for drag-to-select functionality on the map
 * @param {boolean} enabled - Whether box selection is enabled
 * @param {Function} onSelectionComplete - Callback when selection is complete (receives bounds: {north, south, east, west})
 */
const BoxSelector = ({ enabled, onSelectionComplete }) => {
  const map = useMap();
  const isSelectingRef = useRef(false);
  const startPointRef = useRef(null);
  const boxRef = useRef(null);

  // Use useMapEvents for proper event handling
  useMapEvents({
    mousedown: (e) => {
      if (!enabled) return;
      
      // Only start on left click
      if (e.originalEvent.button !== 0) return;
      
      isSelectingRef.current = true;
      const point = e.latlng;
      startPointRef.current = point;

      // Create rectangle element
      if (!boxRef.current) {
        boxRef.current = L.rectangle([[point.lat, point.lng], [point.lat, point.lng]], {
          color: '#2196f3',
          fillColor: '#2196f3',
          fillOpacity: 0.2,
          weight: 2,
          dashArray: '5, 5',
          interactive: false,
        });
        boxRef.current.addTo(map);
      } else {
        boxRef.current.setBounds([[point.lat, point.lng], [point.lat, point.lng]]);
      }

      // Prevent map drag during selection
      map.dragging.disable();
      map.doubleClickZoom.disable();
    },

    mousemove: (e) => {
      if (!enabled || !isSelectingRef.current || !startPointRef.current || !boxRef.current) return;

      const point = e.latlng;
      
      // Update rectangle bounds
      const bounds = L.latLngBounds(
        [startPointRef.current.lat, startPointRef.current.lng],
        [point.lat, point.lng]
      );
      boxRef.current.setBounds(bounds);
    },

    mouseup: (e) => {
      if (!enabled || !isSelectingRef.current || !startPointRef.current || !boxRef.current) return;

      const point = e.latlng;
      
      // Calculate bounds
      const north = Math.max(startPointRef.current.lat, point.lat);
      const south = Math.min(startPointRef.current.lat, point.lat);
      const east = Math.max(startPointRef.current.lng, point.lng);
      const west = Math.min(startPointRef.current.lng, point.lng);

      // Clean up
      boxRef.current.remove();
      boxRef.current = null;
      isSelectingRef.current = false;
      startPointRef.current = null;

      // Re-enable map interactions
      map.dragging.enable();
      map.doubleClickZoom.enable();

      // Call callback with selection bounds
      if (onSelectionComplete) {
        onSelectionComplete({ north, south, east, west });
      }
    },

    mouseleave: () => {
      if (!enabled) return;
      
      if (boxRef.current) {
        boxRef.current.remove();
        boxRef.current = null;
      }
      isSelectingRef.current = false;
      startPointRef.current = null;
      map.dragging.enable();
      map.doubleClickZoom.enable();
    }
  });

  useEffect(() => {
    if (!enabled) {
      // Clean up if disabled
      if (boxRef.current) {
        boxRef.current.remove();
        boxRef.current = null;
      }
      isSelectingRef.current = false;
      startPointRef.current = null;
      map.dragging.enable();
      map.doubleClickZoom.enable();
    }
  }, [enabled, map]);

  return null; // This component doesn't render anything
};

export default BoxSelector;

